import React, { useState, useEffect } from 'react';
import { Modal, Button, DatePicker, Typography, Form, Row } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import './Company.scss';
import { formatDate, hasRole, openNotification } from '../../utils';
import ProposalService from '../../services/ProposalService';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/tr_TR';
import 'dayjs/locale/tr';

dayjs.locale('tr');
const { Text } = Typography;
const { RangePicker } = DatePicker;

const PlanningModal: React.FC<any> = ({ proposalId, isCustomer = false, isModalVisible, setIsModalVisible }: any) => {
    const [isLoading, setIsLoading] = useState(false);
    const [proposalData, setProposalData] = useState<any>(null);
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [isNull, setIsNull] = useState<any>(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isCentralUser, setIsCentralUser] = useState(false);

    useEffect(() => {
        const isUserAdmin = hasRole('ADMIN');
        setIsAdmin(isUserAdmin);

        const isUserCentralUser = hasRole('CENTRAL_USER');
        setIsCentralUser(isUserCentralUser);
    }, [])


    useEffect(() => {
        if (isModalVisible && proposalId) {
            fetchProposalDates();
        }
    }, [isModalVisible, proposalId]);

    const fetchProposalDates = async () => {
        try {
            const response = await ProposalService.get(proposalId);
            if (response?.data) {
                const proposal = response.data;
                setProposalData(proposal);
                if (!isCustomer) {
                    setStartDate(proposal.requestedPlanningStartDate ? dayjs(proposal.requestedPlanningStartDate) : null);
                    setEndDate(proposal.requestedPlanningEndDate ? dayjs(proposal.requestedPlanningEndDate) : null);
                    if (proposal.requestedPlanningStartDate == null) {
                        setIsNull(true)
                    } else {
                        setIsNull(false)
                    }
                } else {
                    if (proposal.requestedPlanningStartDate != null) {
                        setStartDate(proposal.requestedPlanningStartDate ? dayjs(proposal.requestedPlanningStartDate) : null);
                        setEndDate(proposal.requestedPlanningEndDate ? dayjs(proposal.requestedPlanningEndDate) : null);
                    } else if (proposal.approvedPlanningStartDate != null) {
                        setStartDate(proposal.approvedPlanningStartDate ? dayjs(proposal.approvedPlanningStartDate) : null);
                        setEndDate(proposal.approvedPlanningEndDate ? dayjs(proposal.approvedPlanningEndDate) : null);
                    } else {
                        setStartDate(null);
                        setEndDate(null);
                    }
                    if (proposal.requestedPlanningStartDate == null) {
                        setIsNull(true)
                    } else {
                        setIsNull(false)
                    }
                }

            } else {
                setStartDate(null);
                setEndDate(null);
                setIsNull(false)
            }

        } catch (error) {
            openNotification('error', 'Hata', 'Planlama tarihleri getirilemedi.');
            console.error(error);
        }
    };


    const handleCancel = () => {
        setIsModalVisible(false);
        setStartDate(null);
        setEndDate(null);
    };

    const updateProposal = async () => {
        setIsLoading(true);

        if (!startDate || !endDate) {
            openNotification('warning', 'Uyarı', 'Lütfen planlama tarih aralığını seçiniz.');
            setIsLoading(false);
            return;
        }

        let proposalData: any = {
            approvedPlanningStartDate: dayjs(startDate).add(3, 'hour').toISOString(),
            approvedPlanningEndDate: dayjs(endDate).add(3, 'hour').toISOString(),
            statusId: 7,
        };

        if (isCustomer) {
            proposalData = {
                requestedPlanningStartDate: dayjs(startDate).toISOString(),
                requestedPlanningEndDate: dayjs(endDate).toISOString(),
            };
        }

        try {
            await ProposalService.update(proposalId, proposalData);
            if (isAdmin || isCentralUser) {
                openNotification('success', 'Başarılı', 'Planlama yapıldı.');
            } else {
                openNotification('success', 'Başarılı', 'Planlama talebi başarıyla oluşturuldu.');
            }
            setStartDate(null);
            setEndDate(null);
        } catch (error) {
            openNotification('error', 'Hata', 'Planlama tarihleri güncellenirken bir hata oluştu.');
            console.error(error);
        }
        setIsLoading(false);
        setIsModalVisible(false);
    };

    // Bugünden önceki tarihleri seçilemez hale getiriyoruz
    const disabledDate = (current: any) => {
        return current && current < dayjs().startOf('day');
    };

    const handleDateChange = (dates: any) => {
        if (dates && dates.length === 2) {
            setStartDate(dates[0]);
            setEndDate(dates[1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    };

    return (
        <>
            <Modal
                title={<Text className="modal-title">
                    {(isAdmin || isCentralUser ? "Planlama Oluştur" : "Planlama Talebi Oluştur")}
                </Text>}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                className="planlama-modal"
            >
                <Form layout="vertical">
                    {((isAdmin || isCentralUser) && proposalData) && (
                        <p style={{ marginBottom: '10px' }}>
                            <strong>Planlama Talep Edilen Tarih:</strong>
                            {proposalData?.requestedPlanningStartDate && formatDate(proposalData.requestedPlanningStartDate)} -
                            {proposalData?.requestedPlanningEndDate && formatDate(proposalData.requestedPlanningEndDate)}
                        </p>
                    )}
                    <Form.Item label={isAdmin || isCentralUser ? "Planlanan Tarih" : "Talep Edilen Tarih"}>
                        <RangePicker
                            locale={locale}
                            placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                            format="DD/MM/YYYY HH:mm"
                            showTime={{ format: 'HH:mm', minuteStep: 10 }}
                            suffixIcon={<CalendarOutlined />}
                            value={startDate && endDate ? [startDate, endDate] : null}
                            style={{ width: '100%', marginTop: '10px' }}
                            className="date-picker-input"
                            onChange={handleDateChange}
                            disabledDate={disabledDate}
                        />
                    </Form.Item>
                    <Row justify={'center'}>
                        <Button type="primary" loading={isLoading} onClick={updateProposal} className="d-button dark-button width-auto">
                            {isAdmin || isCentralUser ? "Kaydet ve Gönder" : "Talep Oluştur"}
                        </Button>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default PlanningModal;
